import React, { useEffect, useMemo, useState } from "react";
import ModalTabs from "../react/ModalTabs/ModalTabs";
import * as BusinessLogic from "../../lib/BusinessLogic";
import * as t from "../../lib/backend/manufacturing2.generated.types";
import * as t2 from "../../lib/SimulationReportModels";
import * as r from "../../lib/backend/reports.generated.types";
import { Modal, Table } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import * as Auth from "../../lib/Auth";
import local_settings, { FIELD_LAST_PLANT } from "../../lib/LocalSettings";
import { getBackend } from "../../lib/backend/Backend2";
import { translate } from "../IntlProviderWrapper";
import { toSloDateTimeString, niceDate, niceNumber, niceNumberEstimate, niceDateTime, deepClone } from "../../lib/Util";
import { ORDER_TYPE, ORDER_GENERATED_BY, type OrderTypeEnum } from "../../lib/ManufacturingConsts.generated";
import ParallelMD04 from "./ParallelMD04";
import TranslatedMaterialTitle from "../TranslatedMaterialTitle";
import Barcode from "react-barcode";
import {
    PLANT_TAGS,
    PLANT_TAGS_ACCESS,
    ORDER_BOM_INPUT_TAGS,
    ORDER_BOM_INPUT_TAGS_ACCESS,
    ORDER_TAGS,
    ORDER_TAGS_ACCESS,
    ORDER_TAGS_SETTER,
} from "../../lib/ManufacturingTags.generated";
import Authorization from "../Authorization";
import EditOrderModal from "./EditOrderModal";
import SimpleTable from "../react/SimpleTable";
import { cleanExternalId } from "./OrderTableProduction";
import { LinesOrdersLogic } from "./PlanningTable2/reducers/BusinessLogic";
import { LinesOrdersMaps } from "./PlanningTable2/reducers/Mappers";

import "../../styles/order_table_production_modal.scss";
import { MaterialTranslations } from "../MaterialTranslations";

type IOrdersModelBaseExtended = t.IOrderModelBase & {
    quantity_produced?: number;
    quantity_scrap?: number;
};

type IViewOrderBomInputExtended = t.IViewOrderBomInput & {
    process_num: string;
    input_quantity: number;
    is_tool: boolean;
};

type OperationColumn = (keyof Partial<IOrdersModelBaseExtended> | keyof Partial<typeof ORDER_TAGS>) & string;
type BomInputColumn = keyof Partial<IViewOrderBomInputExtended>;

const operationDescriptionFields: OperationColumn[] = [
    "external_id",
    "external_operation_id",
    "material_external_id",
    "material_title",
    "quantity_total",
    "status",
];

const operationDataFields: OperationColumn[] = [
    "title",
    "external_id",
    "external_operation_id",
    "process_num",
    "quantity_total",
    "output_stock",
    "status",
    "norm_base_units",
    "norm_break_time_hours",
    "norm_clearing_time",
    "norm_machine_time",
    "norm_person_time",
    "norm_tool_setup_time",
    "created_at",
    "capacity_factor",
    "earliest_start",
    "earliest_end",
    "time_start",
    "time_end",
    "override_capacity_factor",
    "wait_time_hours",
    "produced_available_in_hours",
    "plant_uuid",
    "process_uuid",
    "sequence_weight",
    "sim_ignore_earliest_start",
    "skip_sim",
    "ignore_on_planning_board",
    "uuid",
    "quantity_scrap",
    "quantity_produced",
];

const bomInputDataFields: BomInputColumn[] = [
    "process_num",
    "input_material_external_id",
    "input_material_title",
    "factor",
    "input_quantity",
    "input_withdrawn_quantity",
    "stock_location_external_id",
    "backflush",
    "is_tool",
];

const Translations = {
    title: ["OrderTableProduction.title", "Operation title"],
    external_id: ["OrderTableProduction.external_id", "Order ID"],
    external_operation_id: ["OrderTableProduction.external_operation_id", "Operation ID"],
    process_num: ["OrderTableProduction.process_num", "Process number"],
    earliest_start: ["OrderTableProduction.earliest_start", "ERP start"],
    earliest_end: ["OrderTableProduction.earliest_end", "ERP completion"],
    quantity_total: ["OrderTableProduction.quantity_total", "Operation total quantity"],
    status: ["OrderTableProduction.status", "Operation status"],
    norm_base_units: ["OrderTableProduction.norm_base_units", "Norm base"],
    norm_break_time_hours: ["OrderTableProduction.norm_break_time_hours", "Break time"],
    norm_clearing_time: ["OrderTableProduction.norm_clearing_time", "Clearing time"],
    norm_machine_time: ["OrderTableProduction.norm_machine_time", "Norm (machine)"],
    norm_person_time: ["OrderTableProduction.norm_person_time", "Norm (person)"],
    norm_tool_setup_time: ["OrderTableProduction.norm_tool_setup_time", "Tool setup time"],
    created_at: ["OrderTableProduction.created_at", "Created at"],
    capacity_factor: ["OrderTableProduction.capacity_factor", "Capacity factor"],
    initial_earliest_end: ["OrderTableProduction.initial_earliest_end", "Initial earliest end time"],
    initial_earliest_start: ["OrderTableProduction.initial_earliest_start", "Initial earliest start time"],
    output_stock: ["OrderTableProduction.output_stock", "Outputs to stock"],
    wait_time_hours: ["OrderTableProduction.wait_time_hours", "End of operation wait time"],
    produced_available_in_hours: ["OrderTableProduction.produced_available_in_hours", "Produced available in hours"],
    override_capacity_factor: ["OrderTableProduction.override_capacity_factor", "Override capacity factor"],
    plant_uuid: ["OrderTableProduction.plant_uuid", "Plant UUID"],
    process_uuid: ["OrderTableProduction.process_uuid", "Process UUID"],
    sequence_weight: ["OrderTableProduction.sequence_weight", "Sequence weight"],
    sim_ignore_earliest_start: ["OrderTableProduction.sim_ignore_earliest_start", "Ignore earliest start"],
    skip_sim: ["OrderTableProduction.skip_sim", "Skip simulation"],
    uuid: ["OrderTableProduction.uuid", "UUID"],
    bom_version: ["OrderTableProduction.bom_version", "BOM version"],
    generated_by: ["OrderTableProduction.generated_by", "Generated by"],
    id: ["OrderTableProduction.id", "ID"],
    minimal_overlap_hours: ["OrderTableProduction.minimal_overlap_hours", "Minimal overlap hours"],
    minimal_overlap_quantity: ["OrderTableProduction.minimal_overlap_quantity", "Minimal overlap quantity"],
    order_type: ["OrderTableProduction.order_type", "Order type"],
    pipeline_production: ["OrderTableProduction.pipeline_production", "Pipeline production"],
    required_overlap: ["OrderTableProduction.required_overlap", "Required overlap"],
    time_end: ["OrderTableProduction.time_end", "End time"],
    time_start: ["OrderTableProduction.time_start", "Start time"],
    line: {
        external_id: ["OrderTableProduction.line_external_id", "Line ID"],
        uuid: ["OrderTableProduction.line_uuid", "Line UUID"],
        title: ["common.line", "Line"],
    },
    material_external_id: ["OrderTableProduction.material_external_id", "Material ID"],
    material_title: ["OrderTableProduction.material_title", "Material title"],
    material_uuid: ["OrderTableProduction.material_uuid", "Material UUID"],
    skip_sim_internal: ["OrderTableProduction.skip_sim", "Skip sim"],
    parent_order_uuid: ["OrderTableProduction.parent_order_uuid", "Parent operation"],
    measurement_unit: ["OrderTableProduction.measurement_unit", "Measurement unit"],
    tags: ["common.tags", "Tags"],
    quantity_scrap: ["common.scrap", "Scrap"],
    quantity_produced: ["OrderTableProduction.quantity_produced", "Quantity produced"],
    // bom inputs
    input_quantity: ["OrderTableProduction.input_quantity", "Input quantity"],
    factor: ["OrderTableProduction.factor", "Factor"],
    input_material_external_id: ["OrderTableProduction.material_external_id", "Material ID"],
    input_material_title: ["OrderTableProduction.material_title", "Material title"],
    input_measurement_unit: ["OrderTableProduction.measurement_unit", "Measurement unit"],
    input_withdrawn_quantity: ["OrderTableProduction.withdrawn_quantity", "Withdrawn quantity"],
    stock_location_external_id: ["OrderTableProduction.stock_location_external_id", "Stock location ID"],
    backflush: ["OrderTableProduction.backflush", "Backflush"],
    is_tool: ["common.tool", "Tool"],
    ignore_on_planning_board: ["common.ignored_operation_abbr", "Ignored"],
};

const getTranslation = (...path: OperationColumn[]): string => {
    let translation = path.join("_");
    let ref = Translations;
    for (const field of path) {
        if (typeof ref === "object" && ref !== null) {
            ref = ref[field];
        } else {
            return translation;
        }
    }

    if (Array.isArray(ref)) {
        const [key, value] = ref;
        if (typeof key === "string" && typeof value === "string") {
            translation = translate(key, value);
        }
    }

    return translation;
};

const getTags = (tags: t.ITags): React.ReactNode => {
    return (
        <div className="order-table-production-modal__tags">
            {Object.keys(tags).map(key => {
                const value = tags[key];
                const classes = ["badge"];
                if (value === "true") {
                    classes.push("badge-success");
                } else if (value === "false") {
                    classes.push("badge-danger");
                } else {
                    classes.push("badge-secondary");
                }

                return (
                    <span key={key} className={classes.join(" ")}>
                        {`${key}: ${value}`}
                    </span>
                );
            })}
        </div>
    );
};

const getBooleanValue = (value: boolean): string => {
    return value ? translate("common.yes", "Yes") : translate("common.no", "No");
};

const getValue = (operation: IOrdersModelBaseExtended, field: OperationColumn): string => {
    // chunk orders are ordered right below their full parents and as such do not need
    // to display process number, to make it more obvious they are "children"
    if (operation.order_type === ORDER_TYPE.chunk && field === "process_num") {
        return "";
    }

    let value = operation[field];
    if (value === undefined && typeof ORDER_TAGS_ACCESS[field] === "function") {
        value = ORDER_TAGS_ACCESS[field](operation.tags);
    }

    if (value === undefined || value === null) {
        return "N/A";
    }

    if (typeof value === "string" || typeof value === "number") {
        const date = new Date(value);

        if (["created_at"].includes(field)) {
            return toSloDateTimeString(date);
        }

        if (
            ["created_at", "earliest_start", "earliest_end", "initial_earliest_end", "initial_earliest_start"].includes(
                field
            )
        ) {
            return niceDate(date);
        }

        if (["time_start", "time_end"].includes(field)) {
            return niceDateTime(date);
        }
    }

    // chunks generated by manual entry subcontractor do not have produced and scrap numbers
    if (operation.generated_by === ORDER_GENERATED_BY.manual_entry_subcontractor) {
        if (["quantity_produced", "quantity_scrap"].includes(field)) {
            return "";
        }
    }

    const number_fields = [
        "produced_available_in_hours",
        "norm_break_time_hours",
        "norm_machine_time",
        "norm_person_time",
        "quantity_scrap",
        "quantity_produced",
        "quantity_total",
        "wait_time_hours",
    ];
    if (number_fields.includes(field)) {
        return niceNumber(value, 3);
    }

    if (field === "output_stock" && operation.produced_available_in_hours > 0) {
        return `${getBooleanValue(value)} (+${niceNumber(operation.produced_available_in_hours, 2)}h)`;
    }

    if (typeof value === "boolean") {
        return getBooleanValue(value);
    }

    const material_external_id = operation["material_external_id"];
    const material_title = operation["material_title"];
    if (field === "material_title") {
        return TranslatedMaterialTitle({ material_external_id, material_title });
    }

    if (field === "status") {
        return translate(`OrderShifts.order_${value}`, `${value}`);
    }

    return value.toString();
};

const getValueBom = (operation: Partial<IViewOrderBomInputExtended>, field: string): string => {
    const quantity_fields = ["input_quantity", "input_withdrawn_quantity"];
    if (quantity_fields.includes(field)) {
        let value = operation[field];
        let measurement_unit: string = operation.input_measurement_unit ?? "";
        const order_bom_input_tags: t.ITags = operation.tags ?? {};
        const original_measurement_unit =
            ORDER_BOM_INPUT_TAGS_ACCESS.original_input_measurement_unit(order_bom_input_tags);
        if (original_measurement_unit !== "" && order_bom_input_tags[ORDER_BOM_INPUT_TAGS.input_factor] !== undefined) {
            measurement_unit = original_measurement_unit;
            const input_factor = ORDER_BOM_INPUT_TAGS_ACCESS.input_factor(order_bom_input_tags);
            const target_factor = ORDER_BOM_INPUT_TAGS_ACCESS.target_factor(order_bom_input_tags);
            if (field === "input_withdrawn_quantity") {
                value /= input_factor;
            } else if (field === "input_quantity") {
                value *= target_factor / input_factor;
            }
        }

        return `${niceNumber(value, 2)} ${measurement_unit}`;
    }

    const small_number_fields = ["factor"];
    if (small_number_fields.includes(field)) {
        return niceNumberEstimate(operation[field], 0.05);
    }

    if (typeof operation[field] === "boolean") {
        return getBooleanValue(operation[field]);
    }

    if (field === "input_material_title") {
        return TranslatedMaterialTitle({
            material_external_id: operation.input_material_external_id ?? "",
            material_title: operation.input_material_title ?? "",
        });
    }

    return operation[field].toString();
};

type MD04ButtonProps = {
    parent_order_uuid: string;
    material_title: string;
    material_external_id: string;
    onSelectedMaterialChange: (material_external_id: string) => void;
};

type MD04ButtonState = {
    show_modal: boolean;
};

class MD04Button extends React.Component<MD04ButtonProps, MD04ButtonState> {
    state = {
        show_modal: false,
    };

    getPlantUuid = () => {
        return local_settings.getItem(FIELD_LAST_PLANT);
    };

    onCloseModal = () => this.setState({ show_modal: false });

    onShowModal = () => this.setState({ show_modal: true });

    render() {
        const plant_uuid = this.getPlantUuid();
        if (!plant_uuid) return null;

        return (
            <>
                <button
                    className="btn btn-primary ml-2"
                    onClick={this.onShowModal}
                    title={translate("common.tooltip_planningtable_requirements", "Show Stock/Requirements situation")}
                    style={{ float: "right" }}
                >
                    {translate("common.requirements", "Requirements")}
                </button>
                <ParallelMD04
                    onCloseModal={this.onCloseModal}
                    plant_uuid={plant_uuid}
                    show_modal={this.state.show_modal}
                    material_external_id={this.props.material_external_id}
                    material_title={this.props.material_title}
                    parent_order_uuid={this.props.parent_order_uuid}
                />
            </>
        );
    }
}

const updateOperation = (operation: IOrdersModelBaseExtended, operation_overrides: OperationDetailsOverrides) => {
    const updated_operation: IOrdersModelBaseExtended = deepClone(operation);
    if (operation_overrides !== undefined) {
        if (operation_overrides.quantity_total !== undefined) {
            updated_operation.quantity_total = operation_overrides.quantity_total;
        }

        if (operation_overrides.skip_sim !== undefined) {
            updated_operation.skip_sim = operation_overrides.skip_sim;
        }

        if (operation_overrides.line_title !== undefined) {
            updated_operation.line.title = operation_overrides.line_title;
        }

        if (operation_overrides.line_external_id !== undefined) {
            updated_operation.line.external_id = operation_overrides.line_external_id;
        }

        if (operation_overrides.line_uuid !== undefined) {
            updated_operation.line.uuid = operation_overrides.line_uuid;
        }

        if (operation_overrides.time_start !== undefined) {
            updated_operation.time_start = operation_overrides.time_start;
        }

        if (operation_overrides.time_end !== undefined) {
            updated_operation.time_end = operation_overrides.time_end;
        }

        if (operation_overrides.uuid !== undefined) {
            updated_operation.uuid = operation_overrides.uuid;
        }

        if (operation_overrides.external_id !== undefined) {
            updated_operation.external_id = operation_overrides.external_id;
        }

        if (operation_overrides.external_operation_id !== undefined) {
            updated_operation.external_operation_id = operation_overrides.external_operation_id;
        }

        if (operation_overrides.ignore_on_planning_board !== undefined) {
            ORDER_TAGS_SETTER.ignore_on_planning_board(
                updated_operation.tags,
                operation_overrides.ignore_on_planning_board
            );
        }
    }

    return updated_operation;
};

const updateOperations = (
    operations: IOrdersModelBaseExtended[],
    operations_overrides: Map<string, OperationDetailsOverrides>
): Map<string, IOrdersModelBaseExtended> => {
    const updated_operations: Map<string, IOrdersModelBaseExtended> = new Map();
    for (const operation of operations) {
        const operation_overrides = operations_overrides.get(operation.uuid);
        const updated_operation: IOrdersModelBaseExtended =
            operation_overrides !== undefined ? updateOperation(operation, operation_overrides) : operation;
        updated_operations.set(operation.uuid, updated_operation);
    }

    return updated_operations;
};

const UpdatedValueInfo = (props: { old_value: unknown; new_value: unknown }) => {
    const { old_value, new_value } = props;
    if (old_value === new_value) {
        return null;
    }

    return <sup className="fas fa-exclamation-circle" title={translate("common.updated_value", "Updated value")} />;
};

type OperationDetailsProps = {
    operations: IOrdersModelBaseExtended[];
    selected_order_uuid: string;
    show_columns?: OperationColumn[];
    report_result?: r.ISimulationReport | t2.ISimulationReportEx;
    operations_overrides?: Map<string, OperationDetailsOverrides>;
};

const OperationDetails = (props: OperationDetailsProps) => {
    const updated_operations: Map<string, IOrdersModelBaseExtended> = useMemo(() => {
        if (props.operations_overrides === undefined) {
            return new Map();
        }

        return updateOperations(props.operations, props.operations_overrides);
    }, [props.operations, props.operations_overrides]);

    if (!props.operations) return null;

    const show_columns = props.show_columns;
    let show_selected_columns = false;
    let operation_columns = operationDataFields;

    if (show_columns && show_columns.length > 0) {
        show_selected_columns = true;
        operation_columns = show_columns;
    }

    const getColumnName = (field: OperationColumn): string => {
        if (field === "skip_sim" && show_selected_columns) {
            return translate("common.scheduled_operation_abbr", "Scheduled operation");
        }

        return getTranslation(field);
    };

    const getColumnValue = (operation: IOrdersModelBaseExtended, field: OperationColumn): string => {
        if (field === "skip_sim" && show_selected_columns) {
            return getBooleanValue(!operation[field]);
        }

        return getValue(operation, field);
    };

    return (
        <Table className="operation-details__table">
            <thead>
                <tr>
                    {operation_columns.map((field, idx) => (
                        <th key={idx}>{getColumnName(field)}</th>
                    ))}
                    <th>{translate(Translations.line.title[0], Translations.line.title[1])}</th>
                    <th>{translate(Translations.line.external_id[0], Translations.line.external_id[1])}</th>
                    <th>{translate("common.comment", "Comment")}</th>
                </tr>
            </thead>
            <tbody>
                {props.operations.map((operation, idx) => {
                    const updated_operation = updated_operations.get(operation.uuid) || operation;
                    return (
                        <tr
                            key={idx}
                            // selected operation is hightlighted and chunks have different style
                            className={
                                operation.uuid === props.selected_order_uuid
                                    ? "tr-sum"
                                    : updated_operation.order_type === ORDER_TYPE.chunk
                                      ? "tr-light"
                                      : ""
                            }
                        >
                            {operation_columns.map((field, idx) => (
                                <td className="text-nowrap" key={idx}>
                                    {getColumnValue(updated_operation, field)}{" "}
                                    <UpdatedValueInfo
                                        old_value={getValue(operation, field)}
                                        new_value={getValue(updated_operation, field)}
                                    />
                                </td>
                            ))}
                            <td className="text-nowrap">
                                {updated_operation.line.title}{" "}
                                <UpdatedValueInfo
                                    old_value={operation.line.title}
                                    new_value={updated_operation.line.title}
                                />
                            </td>
                            <td className="text-nowrap">
                                {updated_operation.line.external_id}{" "}
                                <UpdatedValueInfo
                                    old_value={operation.line.external_id}
                                    new_value={updated_operation.line.external_id}
                                />
                            </td>
                            <td className="text-nowrap">{updated_operation.order_comment}</td>
                        </tr>
                    );
                })}
            </tbody>
        </Table>
    );
};

const updateTranslations = async (input_material_uuids: string[]) => {
    await MaterialTranslations.loadTranslations(input_material_uuids, true);
};

type BomInputDetailsProps = {
    bom_inputs: IViewOrderBomInputExtended[];
    show_columns?: BomInputColumn[];
};

const BomInputDetails = (props: BomInputDetailsProps) => {
    useEffect(() => {
        updateTranslations(props.bom_inputs.map(bom_input => bom_input.input_material_uuid));
    }, [props.bom_inputs]);

    if (!props.bom_inputs) return null;

    const show_columns = props.show_columns;
    let bomInputDataFields2 = bomInputDataFields;
    if (show_columns && show_columns.length > 0) {
        bomInputDataFields2 = show_columns;
    }
    return (
        <Table className="operation-details__table">
            <thead>
                <tr>
                    {(bomInputDataFields2 as OperationColumn[]).map((field, idx) => (
                        <th key={idx}>{getTranslation(field)}</th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {props.bom_inputs.map((bom_input, idx) => {
                    return (
                        <tr key={idx}>
                            {bomInputDataFields2.map((field, idx) => (
                                <td key={idx}>{getValueBom(bom_input, field)}</td>
                            ))}
                        </tr>
                    );
                })}
            </tbody>
        </Table>
    );
};

type BarcodeFieldProps = {
    operation_external_id: string;
    plant_uuid: string;
};

const BarcodeField = (props: BarcodeFieldProps) => {
    const plant = BusinessLogic.getPlantsForUser().find(p => p.uuid === props.plant_uuid);
    if (!plant) {
        return null;
    }

    let show_barcode = false;

    if (plant.tags) {
        show_barcode = plant.tags[PLANT_TAGS.show_barcode] === "true";
    }

    return (
        <div>
            {show_barcode && <Barcode format="CODE39" value={"CL" + cleanExternalId(props.operation_external_id)} />}
        </div>
    );
};

type DescriptionProps = {
    operation?: IOrdersModelBaseExtended;
    barcode_operation?: Partial<t.IOrderModelBase>;
    override_description_fields?: OperationColumn[];
    hide_barcode_operation: boolean;
    operation_overrides?: OperationDetailsOverrides;
};
const Description = (props: DescriptionProps) => {
    const { operation, override_description_fields, barcode_operation } = props;

    if (!operation) return null;
    const updated_operation =
        props.operation_overrides !== undefined ? updateOperation(operation, props.operation_overrides) : operation;

    let operationDescriptionFields2 = operationDescriptionFields;
    if (override_description_fields && override_description_fields.length > 0) {
        operationDescriptionFields2 = override_description_fields;
    }

    const comment = updated_operation.order_comment || "";
    return (
        <>
            <div className="order-table-production-modal__description">
                {operationDescriptionFields2.map((field, idx) => {
                    const name = getTranslation(field);
                    return (
                        <div className="single-description" key={idx}>
                            <span className="title text-muted">{name}</span>
                            <span className="">
                                {getValue(updated_operation, field)}{" "}
                                <UpdatedValueInfo
                                    old_value={getValue(operation, field)}
                                    new_value={getValue(updated_operation, field)}
                                />
                            </span>
                            <br />
                        </div>
                    );
                })}
                {comment && (
                    <div className="single-description">
                        <span className="title text-muted">{translate("common.comment", "Comment")}</span>
                        <span>{comment}</span>
                        <br />
                    </div>
                )}
                {!override_description_fields && (
                    <>
                        <div className="single-description">
                            <span className="title text-muted">
                                {translate(Translations.line.title[0], Translations.line.title[1])}
                            </span>
                            <span>
                                {updated_operation.line.title}{" "}
                                <UpdatedValueInfo
                                    old_value={operation.line.title}
                                    new_value={updated_operation.line.title}
                                />
                            </span>
                            <br />
                        </div>
                        <div className="single-description">
                            <span className="title text-muted">
                                {translate(Translations.line.uuid[0], Translations.line.uuid[1])}
                            </span>
                            <span>
                                {updated_operation.line.uuid}{" "}
                                <UpdatedValueInfo
                                    old_value={operation.line.title}
                                    new_value={updated_operation.line.title}
                                />
                            </span>
                            <br />
                        </div>
                    </>
                )}
            </div>
            <div style={{ textAlign: "center" }}>
                {!props.hide_barcode_operation && barcode_operation && (
                    <BarcodeField
                        operation_external_id={barcode_operation.external_operation_id as string}
                        plant_uuid={barcode_operation.plant_uuid as string}
                    />
                )}
            </div>
        </>
    );
};

/************************************************************************************************ */

type OrderProductionsProps = {
    order_uuid: string;
    show_technical_details: boolean;
};
type OrderProductionsState = {
    data: t.IProducedModel[];
};
const initialOrderProductionsState: OrderProductionsState = {
    data: [],
};
const OrderProductions = (props: OrderProductionsProps) => {
    const [state, setState] = useState(initialOrderProductionsState);

    useEffect(() => {
        (async () => {
            const res = await getBackend().manufacturing.getProducedAll({
                end: Date.now(),
                start: 0,
                order_uuid: props.order_uuid,
            });
            setState({ data: res.data.sort((a, b) => a.ts - b.ts) });
        })();
    }, [props.order_uuid]);

    const data = state.data.map(x => {
        const row = [
            niceDateTime(new Date(x.ts)),
            niceDateTime(new Date(x.available_in_ts)),
            niceNumber(x.quantity_produced, 0),
            niceNumber(x.quantity_scrap, 0),
            x.status,
            x.stock_accounted_for ? translate("common.yes", "Yes") : translate("common.no", "No"),
            x.line_title,
        ];
        if (props.show_technical_details) {
            row.push(x.external_id);
            row.push(x.uuid);
        }
        return row;
    });
    const sum_produced = state.data.reduce((prev, x) => (prev += x.quantity_produced), 0);
    const sum_scrap = state.data.reduce((prev, x) => (prev += x.quantity_scrap), 0);

    const columns = [
        {
            header: translate("OrderTableProduction.produced_at", "Produced at"),
            footer: translate("common.sum", "Sum"),
        },
        {
            header: translate("OrderTableProduction.available_at", "Available at"),
            footer: "",
        },
        {
            header: translate("OrderTableProduction.quantity_produced", "Quantity produced"),
            footer: niceNumber(sum_produced, 0),
        },
        {
            header: translate("OrderTableProduction.quantity_scrap", "Scrap"),
            footer: niceNumber(sum_scrap, 0),
        },
        {
            header: translate("common.status", "Status"),
            footer: "",
        },
        {
            header: translate("OrderTableProduction.stock_accounted_for", "Stock accounted for"),
            footer: "",
        },
        {
            header: translate("common.line", "Line"),
            footer: "",
        },
    ];

    if (props.show_technical_details) {
        columns.push({
            header: translate("common.external_id", "External ID"),
            footer: "",
        });
        columns.push({
            header: translate("common.uuid", "UUID"),
            footer: "",
        });
    }

    return (
        <div className="order-table-production-modal__productions">
            <SimpleTable data={data} header={columns.map(c => c.header)} footer={columns.map(c => c.footer)} />
        </div>
    );
};

/************************************************************************************************ */

type OrderDetailsProps = {
    order_uuid: string;
};

type OrderDetailsState = {
    has_data?: boolean;
    data?: unknown;
    operation?: unknown;
    prods?: string[][];
};

const initialOrderDetailsState: OrderDetailsState = {
    has_data: false,
    data: null,
    operation: null,
    prods: [],
};
const OrderDetails = (props: OrderDetailsProps) => {
    const [state, setState] = useState<OrderDetailsState>(initialOrderDetailsState);

    const refresh = async () => {
        const { operations } = await getBackend().manufacturing.getOrdersSimple({ uuids: [props.order_uuid] });
        if (operations.length == 0) {
            setState({ data: [] });
            return;
        }
        const operation = operations[0];
        setState({ operation });
    };
    return (
        <div>
            <div className="my-2 mr-2">
                <button className="btn" onClick={refresh}>
                    Refresh
                </button>
            </div>
            <h2>Order</h2>
            <pre>{JSON.stringify(state.operation, null, "  ")}</pre>
        </div>
    );
};

/************************************************************************************************ */

type OrderInsightsProps = {
    order_uuid: string;
};

type OrderInsightsState = {
    insights: t.IEventDataEx[] | null;
    has_data: boolean;
    data: unknown;
    operation: unknown;
    prods: string[][];
};
const initialOrderInsightsState: OrderInsightsState = {
    has_data: false,
    data: null,
    insights: null,
    operation: null,
    prods: [],
};
const OrderInsights = (props: OrderInsightsProps) => {
    const [state, setState] = useState(initialOrderInsightsState);

    const refresh = async () => {
        const { insights } = await getBackend().manufacturing.getInsights({
            operations: [props.order_uuid],
        });
        setState(prevState => ({ ...prevState, insights }));
    };

    return (
        <div>
            <div className="my-2 mr-2">
                <button className="btn" onClick={refresh}>
                    Refresh
                </button>
            </div>
            <Table className="operation-details__table">
                <thead>
                    <tr>
                        <th>Time</th>
                        <th>Title</th>
                        <th>Description</th>
                        <th>Type</th>
                    </tr>
                </thead>
                <tbody>
                    {state.insights &&
                        state.insights.map((x, index) => {
                            return (
                                <tr key={index}>
                                    <td>{niceDateTime(x.ts)}</td>
                                    <td>{x.title}</td>
                                    <td>{x.description}</td>
                                    <td>{x.type}</td>
                                </tr>
                            );
                        })}
                </tbody>
            </Table>
        </div>
    );
};

/************************************************************************************************ */

type OrderSimulationProps = {
    order_uuid: string;
    report_uuid?: string;
};

type OrderSimulationState = {
    data: r.ISimulationReportOrder | r.ISimulationReportOrder[] | null;
    prods: string[][];
};
const initialOrderSimulationState: OrderSimulationState = {
    data: null,
    prods: [],
};
const OrderSimulation = (props: OrderSimulationProps) => {
    const [state, setState] = useState(initialOrderSimulationState);
    const { order_uuid, report_uuid } = props;

    const refresh = async () => {
        const { operations } = await getBackend().manufacturing.getOrdersSimple({ uuids: [order_uuid] });
        if (operations.length == 0) {
            setState({ data: [], prods: [] });
            return;
        }
        const operation = operations[0];
        let line_uuid = operation.line.uuid;
        let sim_operation_uuid = operation.uuid;

        if (LinesOrdersLogic.isConvertedOperationParent(operation.uuid)) {
            const converted_operation_uuid = LinesOrdersLogic.generateConvertedOrderId(operation.uuid);
            const converted_operation = LinesOrdersMaps.converted_operations.get(converted_operation_uuid);
            if (converted_operation !== undefined) {
                sim_operation_uuid = converted_operation.uuid;
                line_uuid = converted_operation.line_uuid;
            } else {
                const converted_extra_operation =
                    LinesOrdersMaps.converted_extra_operations.get(converted_operation_uuid);
                if (converted_extra_operation !== undefined) {
                    sim_operation_uuid = converted_extra_operation.uuid;
                    line_uuid = converted_extra_operation.line.uuid;
                }
            }
        }

        const report_res = report_uuid
            ? await getBackend().reports.getReport({ id: report_uuid, line_uuids: line_uuid })
            : await getBackend().reports.getLatestReport({ line_uuids: line_uuid });

        const sim_line = report_res.report.result.orders[0];
        if (sim_line == undefined) {
            setState({ data: [], prods: [] });
            return;
        }
        let sim_operation = sim_line.production.filter(x => x.order_id == sim_operation_uuid)[0];
        // if not found, check unscheduled
        if (sim_operation == undefined) {
            const sim_line_unscheduled = report_res.report.result.unscheduled;
            // @ts-ignore - Type 'ISimulationReportUnscheduledOrder[]' is missing properties from type 'ISimulationReportOrder'
            sim_operation = sim_line_unscheduled.filter(x => x.order_uuid == sim_operation_uuid);
        }
        if (sim_operation == undefined) {
            setState({ data: [], prods: [] });
            return;
        }
        const prods: string[][] = [];
        for (const shift of report_res.report.result.line_shift_features[0].shifts) {
            for (const prod of shift.produced.filter(x => x.order_id == sim_operation_uuid)) {
                prods.push([
                    shift.shift_time.shift_date,
                    "" + shift.shift_time.year,
                    "" + shift.shift_time.week,
                    "" + shift.shift_time.shift_number,
                    "" + prod.quantity_predicted.map(x => x.val).join(" / "),
                    "" + prod.tool_setup.toString(),
                ]);
            }
        }
        setState({ data: sim_operation, prods });
    };
    const prods_header = ["date", "year", "week", "shift", "quantity", "tool_setup"];
    return (
        <div>
            <div className="my-2 mr-2">
                <button className="btn" onClick={refresh}>
                    Refresh
                </button>
            </div>
            <h2>Order from simulation</h2>
            <pre>{JSON.stringify(state.data, null, "  ")}</pre>
            <h2>Predicted productions</h2>
            <SimpleTable data={state.prods} header={prods_header} />
        </div>
    );
};

/************************************************************************************************ */

type OperationTagsProps = {
    operations: IOrdersModelBaseExtended[];
};

const OperationTags = (props: OperationTagsProps) => (
    <Table className="operation-details__table">
        <thead>
            <tr>
                <th>{translate(Translations.uuid[0], Translations.uuid[1])}</th>
                <th>{translate(Translations.process_num[0], Translations.process_num[1])}</th>
                <th>{translate(Translations.tags[0], Translations.tags[1])}</th>
            </tr>
        </thead>
        <tbody>
            {props.operations.map(operation => (
                <tr key={operation.uuid}>
                    <td>{operation.uuid}</td>
                    <td>{operation.process_num}</td>
                    <td>{getTags(operation.tags)}</td>
                </tr>
            ))}
        </tbody>
    </Table>
);

/************************************************************************************************ */

export type OperationDetailsOverrides = {
    quantity_total?: number;
    skip_sim?: boolean;
    line_uuid?: string;
    line_external_id?: string;
    line_title?: string;
    time_start?: number;
    time_end?: number;
    external_id?: string;
    external_operation_id?: string;
    uuid?: string;
    ignore_on_planning_board?: boolean;
};

type OrderTableProductionModalProps = {
    selected_order_uuid: string;
    selected_order_external_id: string;
    process_num: string;
    show: boolean;
    onShowOrHideOrderModal: Function;
    show_columns?: OperationColumn[];
    bom_inputs_show_columns?: BomInputColumn[];
    override_description_fields?: OperationColumn[];
    onOrderSave?: (order: t.IOrderModelBase) => void;
    show_edit_button?: boolean;
    report_uuid?: string;
    report?: t2.ISimulationReportEx | null;
    operation_details_overrides?: Map<string, OperationDetailsOverrides>;
};

type OrderTableProductionModalState = {
    operations: IOrdersModelBaseExtended[];
    bom_inputs: IViewOrderBomInputExtended[];
    insights: t.IEventDataEx[];
    selected_order_uuid: string;
    override_description_fields: string[];
    selected_order_external_id: string;
    material_external_id: string | null;
    material_title: string | null;
    show_edit_modal: boolean;
    show_edit_button: boolean;
    show_technical_details: boolean;
    result?: r.ISimulationReport | t2.ISimulationReportEx;
};

class OrderTableProductionModal extends React.Component<
    OrderTableProductionModalProps,
    OrderTableProductionModalState
> {
    state: OrderTableProductionModalState = {
        operations: [],
        bom_inputs: [],
        insights: [],
        selected_order_uuid: "",
        selected_order_external_id: "",
        override_description_fields: [],
        material_external_id: null,
        material_title: null,
        show_edit_modal: false,
        show_edit_button: true,
        show_technical_details: false,
        result: undefined,
    };

    componentDidMount() {
        if (this.props.selected_order_uuid) {
            this.load();
        }
    }

    componentDidUpdate(prev_props: OrderTableProductionModalProps) {
        if (
            prev_props.selected_order_uuid !== this.props.selected_order_uuid ||
            prev_props.selected_order_external_id !== this.props.selected_order_external_id ||
            prev_props.process_num !== this.props.process_num
        ) {
            this.load();
        }
    }

    componentWillUnmount() {
        this.setState({
            operations: [],
            bom_inputs: [],
            selected_order_uuid: "",
            override_description_fields: [],
            material_external_id: null,
            material_title: null,
        });
    }

    load = async () => {
        /*
            Order modal is used on
            - gantt chart (micorplan, gantt, microplan edit quick sim),
            - planning table (lines orders list and gantt chart)
            - microplan (eye icon)
            Different scenarios:
            - user selects chunk order, should show parent order and show it on top
            - user selects parent of chunk order, should show parent order
            - user selects parent parallel order - should show parent order and disable edit
            - user selects child of parallel order - should show child parallel order
        */
        const order_types = Object.keys(ORDER_TYPE).filter(type => ![ORDER_TYPE.chunk].includes(type as OrderTypeEnum));

        let res_order = await getBackend().manufacturing.getOrdersSimple({
            uuids: [this.props.selected_order_uuid],
            order_types: order_types,
        });

        let order = res_order.operations[0];
        if (!order) {
            let res_order = await getBackend().manufacturing.getOrdersSimple({
                order_external_id: this.props.selected_order_external_id,
                process_num: this.props.process_num,
                order_types: order_types,
            });
            order = res_order.operations[0];
        }

        if (!order) {
            console.log(
                "Could not find order OrderTableProductionModa external id: ",
                this.props.selected_order_external_id
            );
            return;
        }

        let show_edit_button =
            order.order_type !== ORDER_TYPE.parallel ||
            (order.order_type === ORDER_TYPE.parallel && order.tags[ORDER_TAGS.parallel_order] === "true");

        let selected_order_uuid = order.uuid;
        const res = await getBackend().manufacturing.getOrdersSimple({
            order_external_id: order.external_id,
        });

        const order_uuids = res.operations.map(o => o.uuid);
        const { orders_produced } = await getBackend().manufacturing.getOrdersProduced({ order_uuids });

        let state_orders: IOrdersModelBaseExtended[] = [];

        for (const operation of res.operations) {
            const produced_order = orders_produced.find(o => o.order_uuid === operation.uuid);
            let ext = {};

            if (produced_order) {
                ext = {
                    quantity_produced: produced_order.quantity_produced,
                    quantity_scrap: produced_order.quantity_scrap,
                };
            } else {
                ext = {
                    quantity_produced: 0.0,
                    quantity_scrap: 0.0,
                };
            }

            state_orders.push({ ...operation, ...ext });
        }
        state_orders.sort((o1, o2) =>
            o1.process_num === o2.process_num
                ? o1.order_type === ORDER_TYPE.chunk
                    ? 1
                    : -1 // when more numbers equal, show non-chunk ones first
                : parseFloat(o1.process_num) - parseFloat(o2.process_num)
        );

        // get BOM for operations
        const res_bom = await getBackend().manufacturing.getInputMaterials({
            order_external_id: order.external_id,
        });

        const bom_inputs: IViewOrderBomInputExtended[] = [];
        for (const bom_input of res_bom.input_materials) {
            // no chunk BOMs
            const bom_input_order = state_orders.find(
                so => so.uuid === bom_input.order_uuid && so.order_type !== ORDER_TYPE.chunk
            );
            if (bom_input_order) {
                bom_inputs.push({
                    ...bom_input,
                    process_num: bom_input_order.process_num,
                    input_quantity: bom_input_order.quantity_total * bom_input.factor,
                    is_tool: ORDER_BOM_INPUT_TAGS_ACCESS.is_tool(bom_input.tags),
                });
            }
        }
        bom_inputs.sort((bi1, bi2) => parseFloat(bi1.process_num) - parseFloat(bi2.process_num));

        const plant_uuid = this.getPlantUuid();
        if (!plant_uuid) return;
        const all_lines = await getBackend().manufacturing.getLines({ plant_uuid });
        // Get unique line uuids and store them in a string
        const all_line_uuids = all_lines.lines.map(l => l.uuid).join(",");

        const operation = state_orders[0];
        state_orders = await this.updateChunkOrders(state_orders);

        const report_uuid = this.props.report_uuid;
        let result: r.ISimulationReport | t2.ISimulationReportEx;
        if (this.props.report) {
            result = this.props.report;
        } else {
            const report_res = report_uuid
                ? await getBackend().reports.getReport({ id: report_uuid, line_uuids: all_line_uuids })
                : await getBackend().reports.getLatestReport({ line_uuids: all_line_uuids });
            result = report_res.report.result;
        }

        this.setState({
            operations: state_orders,
            bom_inputs,
            selected_order_uuid,
            material_external_id: operation.material_external_id,
            show_edit_button,
            result,
        });
    };

    getPlantUuid = () => {
        return local_settings.getItem(FIELD_LAST_PLANT);
    };

    updateChunkOrders = async (orders: IOrdersModelBaseExtended[]): Promise<IOrdersModelBaseExtended[]> => {
        const parent_order_uuids = new Set<string>();
        for (const order of orders) {
            if (order.parent_order_uuid) {
                parent_order_uuids.add(order.parent_order_uuid);
            }
        }
        if (parent_order_uuids) {
            const res = await getBackend().manufacturing.getOrdersSimple({
                uuids: [...parent_order_uuids],
            });
            const parent_orders = res.operations;
            const parent_uuids_comments = new Map<string, string>();
            for (const parent_order of parent_orders) {
                // get parent comments
                const comment = parent_order.order_comment;
                if (comment) {
                    parent_uuids_comments.set(parent_order.uuid, comment);
                }
            }
            // update chunk orders
            for (const order of orders) {
                if (order.order_type === ORDER_TYPE.chunk && order.parent_order_uuid) {
                    const parent_comment = parent_uuids_comments.get(order.parent_order_uuid);
                    if (parent_comment) {
                        // get parent comments
                        order.order_comment = parent_comment;
                    }
                }
            }
        }
        return orders;
    };

    onSelectedMaterialChange = (material_external_id: string) => {
        this.setState({ material_external_id });
    };

    getIsParallel = (): boolean => {
        return this.state.operations.some(o => o.tags.parallel_order === "true" || o.tags.parallel_parent === "true");
    };

    getOperation = (): IOrdersModelBaseExtended | void => {
        return this.state.operations.find(o => o.uuid === this.state.selected_order_uuid);
    };

    onCloseEditModal = () => {
        this.setState({ show_edit_modal: false });
        this.load();
    };

    onShowEditModal = () => {
        this.setState({ show_edit_modal: true });
    };

    onShowTechnicalDetails = () => {
        this.setState(prev_state => ({
            ...prev_state,
            show_technical_details: !prev_state.show_technical_details,
        }));
    };

    showEditButton = () => {
        // we can either entirely hide edit button before mounting the component
        // or we can choose to hide it based on the order selected
        if (this.props.show_edit_button === false || this.state.show_edit_button === false) {
            return false;
        }
        return true;
    };

    showRequirementsButton = () => {
        const plant_uuid = this.getPlantUuid();
        const plant_tags = BusinessLogic.getPlantTags(plant_uuid || "");

        const show_requirements_button = PLANT_TAGS_ACCESS.show_requirements_button(plant_tags);
        return show_requirements_button && this.props.override_description_fields && !this.state.show_technical_details;
    };

    getSelectedOrderUuid = () => {
        return this.state.selected_order_uuid;
    };

    render() {
        if (this.state.operations.length === 0) return null;

        const selected_order_uuid = this.getSelectedOrderUuid();
        const is_admin = Auth.isInRole(Auth.ROLE_ADMIN);
        const operation = this.getOperation();
        const is_parallel = this.getIsParallel();

        const tabs = [
            {
                title: translate("OrderTableProduction.operations", "Operations"),
                element: (
                    <OperationDetails
                        operations={this.state.operations}
                        selected_order_uuid={selected_order_uuid}
                        show_columns={!this.state.show_technical_details ? this.props.show_columns : []}
                        report_result={this.state.result}
                        operations_overrides={this.props.operation_details_overrides}
                    />
                ),
            },
            {
                title: translate("OrderTableProduction.bom", "BOM"),
                element: (
                    <BomInputDetails
                        bom_inputs={this.state.bom_inputs}
                        show_columns={!this.state.show_technical_details ? this.props.bom_inputs_show_columns : []}
                    />
                ),
            },
            {
                title: translate("common.productions", "Productions"),
                element: (
                    <OrderProductions
                        order_uuid={selected_order_uuid}
                        show_technical_details={this.state.show_technical_details}
                    />
                ),
            },
        ];
        if (is_admin) {
            tabs.push({
                title: translate("common.details", "Details"),
                element: <OrderDetails order_uuid={selected_order_uuid} />,
            });
            tabs.push({
                title: translate("common.insights", "Insights"),
                element: <OrderInsights order_uuid={selected_order_uuid} />,
            });
            tabs.push({
                title: translate("common.simulation", "Simulation"),
                element: <OrderSimulation report_uuid={this.props.report_uuid} order_uuid={selected_order_uuid} />,
            });
            tabs.push({
                title: translate("common.tags", "Tags"),
                element: <OperationTags operations={this.state.operations} />,
            });
        }
        return (
            <>
                {this.showEditButton() && this.state.show_edit_modal && (
                    <EditOrderModal
                        key={this.state.selected_order_uuid}
                        show_modal={this.state.show_edit_modal}
                        onCloseModal={this.onCloseEditModal}
                        order_uuid={this.state.selected_order_uuid}
                        onSave={this.props.onOrderSave}
                    />
                )}
                <Modal
                    className="order-table-production-modal"
                    show={this.props.show}
                    size="lg"
                    onHide={() => this.props.onShowOrHideOrderModal(false, selected_order_uuid)}
                >
                    <Modal.Header>
                        <Modal.Title>
                            <FormattedMessage id="OrderTableProduction.order_details" defaultMessage="Order Details" />
                        </Modal.Title>

                        <button
                            type="button"
                            className="close"
                            onClick={() => this.props.onShowOrHideOrderModal(false, selected_order_uuid)}
                        >
                            <span>×</span>
                        </button>
                    </Modal.Header>
                    <Modal.Body>
                        {this.state.operations && this.state.bom_inputs && operation !== undefined && (
                            <ModalTabs
                                details1={
                                    <Description
                                        operation={operation}
                                        barcode_operation={operation}
                                        override_description_fields={
                                            !this.state.show_technical_details
                                                ? this.props.override_description_fields
                                                : []
                                        }
                                        hide_barcode_operation={is_parallel}
                                        operation_overrides={
                                            this.props.operation_details_overrides &&
                                            this.props.operation_details_overrides.get(selected_order_uuid)
                                        }
                                    />
                                }
                                tabs={tabs}
                            />
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                            <div>
                                {this.showEditButton() && (
                                    <Authorization.button
                                        permission={Auth.PERMISSION_NAMES.OrderEdit}
                                        className="btn btn-primary"
                                        onClick={this.onShowEditModal}
                                        style={{ float: "right" }}
                                    >
                                        {translate("common.edit_comment", "Edit comment")}
                                    </Authorization.button>
                                )}
                            </div>
                            <div>
                                <button className="btn btn-primary ml-2" onClick={this.onShowTechnicalDetails}>
                                    {this.state.show_technical_details
                                        ? translate(
                                              "OrderTableProduction.hide_technical_details",
                                              "Hide technical details"
                                          )
                                        : translate("OrderTableProduction.technical_details", "Technical details")}
                                </button>
                                {this.showRequirementsButton() && operation ? (
                                    <MD04Button
                                        parent_order_uuid={operation.uuid}
                                        material_external_id={operation.material_external_id}
                                        material_title={TranslatedMaterialTitle({
                                            material_external_id: operation.material_external_id,
                                            material_title: operation.material_title,
                                        })}
                                        onSelectedMaterialChange={this.onSelectedMaterialChange}
                                    />
                                ) : null}
                            </div>
                        </div>
                    </Modal.Footer>
                </Modal>
            </>
        );
    }
}

export default OrderTableProductionModal;
